/*
 * Normally we cannot detect whether an element is truncated
 * with an ellipsis. This hook uses a resize observer to measure
 * the element and compare its dimensions with its full size,
 * and returns a boolean to let you know whether the text
 * is truncated or not. Works with both traditional one-line
 * text truncation and multiple lines
 *
 * Note that this only works based on some assumptions on
 * how the elements are styled (like hiding overflow or
 * preventing text from wrapping)
 */
import {useState, useRef, useCallback} from 'react';
import useResizeObserver from '../useResizeObserver';

export default function useTruncated({truncatedLines = 1, disable} = {}) {
  const [dimensions, setDimensions] = useState();
  const el = useRef();

  const handleEntry = useCallback(
    (entry) =>
      setDimensions({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      }),
    []
  );

  const {setRef, isSupported} = useResizeObserver({
    handleEntry,
    disable,
  });

  const isTruncated = (() => {
    if (dimensions) {
      if (truncatedLines === 1) {
        // truncated width less than full width of the text
        return dimensions.width < el.current?.scrollWidth;
      } else if (truncatedLines > 1) {
        // For multiple lines, we want to measure that
        // the truncated height is less than the full height
        return dimensions.height < el.current?.scrollHeight;
      }
    }
    return false;
  })();

  function setTruncatedNode(node) {
    setRef(node);
    el.current = node;
  }

  return {isTruncated, setTruncatedNode, isSupported};
}
